import React, { useState, useEffect } from "react";
import Card from "../cards/Card";
import { useCMTag } from "api/api";
import getAverage from "utility/getAverage";
import TickValue from "components/basic/TickValue";
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from "recharts";
import { COP_Small, TimeOption } from "model/Datatypes";
import { stdTimeOptions, dayTimeOption } from "utility/timeOptions";

type COPGraphPoint = {
  COP: number;
};

export const COPCard: React.FC<{ tags: COP_Small }> = ({ tags }) => {
  const [time, setTime] = useState<TimeOption>(dayTimeOption);
  const [COPGraphData, setCOPGraphData] = useState<COPGraphPoint[]>([{ COP: 0 }]);
  const { avgEfficiency, avgCop, loading: COPLoading } = useCOPData(time, tags.tags.COP);

  useEffect(() => {
    setCOPGraphData([{ COP: avgCop ? avgCop : 0 }]);
  }, [avgCop]);

  return (
    <Card
      timeOptions={stdTimeOptions}
      onNewTimeSelected={(newTime) => {
        setTime(newTime);
      }}
      flipdropdown={true}
      timeSelected={time}
      loading={COPLoading}
      size="full"
    >
      <div className="w-full h-full relative animate-margin z-10">
        <div className="absolute bottom-14 2xl:bottom-52 left-1/2 transform -translate-x-1/2 text-gray-500">
          <div className={`text-cyan font-bold`}>
            {avgEfficiency ? (
              <div className="text-3xl 2xl:text-7xl">
                <TickValue value={avgEfficiency} toFixed={0} />%
              </div>
            ) : (
              ".."
            )}
            <div className="mt-1 2xl:mt-3 text-gray-500 text-sm 2xl:text-4xl">Efficiency</div>
          </div>
        </div>
        <div className="z-10 h-full w-full">
          <COPRadialChart data={COPGraphData} />
        </div>

        <div className="absolute bottom-2 2xl:bottom-6 2xl:left-8 text-xxs 2xl:text-2xl text-gray-600">
          * Energy delivered compared to electricity used
        </div>
      </div>
    </Card>
  );
};

export default COPCard;

const COPRadialChart: React.FC<{ data: COPGraphPoint[] }> = ({ data }) => {
  return (
    <ResponsiveContainer>
      <RadialBarChart
        innerRadius={"75%"}
        outerRadius="110%"
        data={data}
        startAngle={200}
        endAngle={-20}
        baseValue={20}
      >
        <PolarAngleAxis
          type="number"
          //@ts-ignore
          domain={[0, 6]}
          tick={false}
        />
        <RadialBar
          background
          dataKey="COP"
          style={{
            fill: "#2FD2A6",
            transition: "fill 1s ease-in-out",
          }}
        />
      </RadialBarChart>
    </ResponsiveContainer>
  );
};

const useCOPData = (time: TimeOption, cop: string) => {
  const [avgCop, setAvgCop] = useState<null | number>(null);
  const [avgEfficiency, setAvgEfficiency] = useState<null | number>(null);
  const [currentCOP, setCurrentCOP] = useState<null | number>(null);
  const [currentEfficiency, setCurrentEfficiency] = useState<null | number>(null);

  const { cmData, loading } = useCMTag(
    cop,
    time.from,
    time.to,
    time.groupingsType,
    true,
    60000
  );
  useEffect(() => {
    if (cmData && cmData.points.length > 0) {
      const avg = getAverage(cmData.points.map((p) => p.value).filter((v) => v > 0 && v < 10));
      setAvgCop(avg);
      setAvgEfficiency(avg * 100);
      setCurrentCOP(cmData.points[cmData.points.length - 1].value);
      setCurrentEfficiency(cmData.points[cmData.points.length - 1].value * 100);
    } else {
      setAvgCop(null);
      setCurrentCOP(null);
      setAvgEfficiency(null);
      setCurrentEfficiency(null);
    }
  }, [cmData, cop]);

  return { avgCop, currentCOP, avgEfficiency, currentEfficiency, loading };
};
